import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { useAuth } from '../../shared/context/AuthContext'
import { useCreateDiscountCouponMutation } from './hooks/useDiscountCouponQuery'
import { ADDITIONAL_RESOURCE_OPTIONS } from './discountCouponConstants'

/**
 * Port of `app/discountCoupon/discountCouponsAdd.html` +
 * `app/discountCoupon/discountCouponsAddController.js` (AngularJS
 * `discountCouponsAddController`, 395 lines — the most complete of the four
 * "add coupon" flows) — the standard/manual "Adicionar Cupom de Desconto"
 * form, reached at `/workspace/discount-coupons/add` (legacy state
 * `workspace.discountCouponsAdd`, `AppRoutes.jsx`). Task 032 scope only; the
 * three sibling variants (automatic/ambassador/combo,
 * `/workspace/discount-coupons/add/{automatic,ambassador,combo}`) are tasks
 * 033/034's scope and live in their own files.
 *
 * `DiscountCouponsService.save` was ported to `useCreateDiscountCouponMutation`
 * in `hooks/useDiscountCouponQuery.js` (task 031's file), not duplicated here.
 *
 * Field-by-field parity notes (legacy -> React):
 * - `category` (select, required, `ng-init` default `BY_NEW_PLAN`): drives
 *   nearly every other field's visibility below, ported 1:1.
 * - `fromDate` / `dueDate`: legacy uses a jQuery `mask` directive
 *   (`mask='39/19/9999'`, i.e. free-text `dd/mm/yyyy` with digit masking) —
 *   no such input-masking library is a project dependency, and every other
 *   ported module with a date field (`ActionAuditPage.jsx`) already
 *   established the native MUI `TextField type="date"` replacement for
 *   Materialize/jQuery date pickers. Reproduced the same way here: state is
 *   still kept/submitted as `dd/mm/yyyy` strings (matching
 *   `discountCoupon.fromDate`/`dueDate`'s shape read by `save()` and
 *   `onSelectCategory()` below), converted to/from the native input's
 *   `yyyy-mm-dd` value only at the input boundary.
 * - `discountCoupon.fromDate` defaults to today (`buildStartDate()`), ported
 *   as `buildStartDate()` below (same `dd/mm/yyyy` format, zero-padded).
 * - `validFor` (visible when `isByNewPlanOrAdditionalResource`): default
 *   `ONE_MONTH`.
 * - `type` (shown for `isNewPlanOrServiceOrAdditionalResource` with "Apenas
 *   clientes selecionados/Todos os clientes" labels): the `BY_PLAN` category
 *   and its own `type` select ("Único/Todos os Tenants") were removed
 *   entirely — that category is no longer offered.
 * - `discountType`: legacy renders this as a *disabled* select permanently
 *   fixed to `BY_VALUE` (only one `<option>` exists) — ported as a
 *   non-interactive, always-`BY_VALUE` field (disabled `Select`), not
 *   simplified away, since `save()` still reads `discountCoupon.discountType`
 *   in several branches (`verifyServiceCoupon`, `canApplyMinimumUpgradeValue`).
 * - `couponUsageType` (visible when `category === 'BY_NEW_PLAN'`): default
 *   `EXCLUSIVE`.
 * - `profileAllowed` (visible when `category === 'BY_NEW_PLAN'`): default
 *   `BOTH`.
 * - `plans` (visible when `category=='BY_NEW_PLAN'`): legacy renders each
 *   plan as a clickable `.chip.custom_chip` toggled via
 *   `onSelectPlan($event, plan.key)` (adds/removes `plan.key` from the
 *   `discountCoupon.plans` array, no native `<select>` involved, visual
 *   state driven by the `.custom_chip_selected` class from `app.scss`) —
 *   ported as clickable MUI `Chip`s with the exact legacy colors applied via
 *   `sx` (`$chip-bg-color`/`custom_chip_selected` background `#E8DEF8` +
 *   `#6A1B9A` border when selected, `custom_chip`'s `#79747E` outline
 *   otherwise — task 035 fix, `_variables.scss`/`app.scss`), same
 *   toggle-on-click semantics (`$scope.onSelectPlan`).
 * - `periodicity` (visible for `BY_ADDITIONAL_RESOURCE` or `BY_NEW_PLAN`):
 *   Mensal/Anual options.
 * - `isNewPlanOrServiceOrAdditionalResource` block (`type`): resets `type` to
 *   `BY_TENANT` on every category change (`handleCategoryChange`).
 * - `limitUsage` (visible when `(category == 'BY_NEW_PLAN' || category ==
 *   'BY_SERVICE') && type != 'BY_TENANT'`): a switch; unchecking clears
 *   `useQuantityLimit` (`onSelectLimitUsageToggle`). `useQuantityLimit`
 *   (number, required) only rendered while `limitUsage` is on.
 * - `userIdentification` + `emails`/`tenantIds` chips (visible when
 *   `(type == 'BY_TENANT' || category == 'BY_SERVICE') && type !=
 *   'ALL_TENANTS'`): legacy uses a Materialize `material_chip` jQuery plugin
 *   wired to raw DOM events (`chip.add`/`chip.delete`) plus an
 *   `<input id="emails-tenants-input">`; free typing doesn't add chips in the
 *   legacy widget (only paste, via `watchEmailsTenantsInputPaste`, or the
 *   library's own Enter-key handling, which isn't reachable without the
 *   library). Ported as a text field + "Adicionar" action (Enter key or
 *   button) appending one validated value at a time as an MUI `Chip`, plus a
 *   paste handler that splits pasted whitespace-separated text into multiple
 *   validated chips at once — reproducing `watchEmailsTenantsInputPaste`'s
 *   validation (`validateEmail`/`validateTenant`, de-dup, cross-list
 *   exclusion) and `replaceEmailsAndTenants`'s swap-on-toggle behavior
 *   (switching `userIdentification` moves the current chip list to the other
 *   field and clears placeholder/label, ported as `handleUserIdentification
 *   Change`).
 * - `additionalResourceType` + `additionalResourceQuantity` (visible when
 *   `category === 'BY_ADDITIONAL_RESOURCE'`): ported the
 *   `ADDITIONAL_RESOURCE_OPTIONS`/`ADDITIONAL_RESOURCES_QUANTITY` constants and
 *   `getAvailableQuantities`/`onAdditionalResourceTypeChange` (resets
 *   quantity on type change) 1:1, including the `WHATSAPP_INTEGRATION`
 *   special-case labeling ("Ilimitado" instead of "1 unidades").
 * - `couponClass` (visible when `isPlanOrAdditionalResource`): default
 *   `CL_REGULAR_TEMPORARY`; `onSelectCombo()` sets `code` to a random hash
 *   (prefixed `combo-` only for `CL_COMBO`) — ported as `createHash`/
 *   `handleCouponClassChange`.
 * - `code` (text, required): user-editable at all times (the combo-class
 *   auto-fill above only *pre-fills* it via the change handler, same as
 *   legacy's one-way `ng-model` set from a click handler).
 * - `discountValue` (visible when `discountType == 'BY_VALUE' || category ==
 *   'BY_SERVICE'`, always true here since `discountType` is fixed) +
 *   `minimumUpgradeValue` (visible when `canApplyMinimumUpgradeValue`: only
 *   for `category === 'BY_NEW_PLAN'` with `couponUsageType === 'EXCLUSIVE'`
 *   and `profileAllowed === 'EDITING'`) — both `type="number"`, `min="1"`,
 *   `step="any"` ported as-is.
 * - `departament` (default `DEP_COMERCIAL`) + `reason` (visible only for
 *   `DEP_CUSTOMER_EXPERIENCE`, default `PLAN_DEAL`).
 * - `vendor` (required): ported from `useAuth().getUserName()`. Left
 *   editable afterwards, exactly like legacy (`ng-init` only sets the
 *   initial value, no `ng-readonly`).
 * - `observation` (optional, free text).
 * - Submit (`save(discountCoupon)`): validates `dueDate` is strictly in the
 *   future (`Materialize.toast` + abort otherwise — note the legacy bug: on
 *   this specific rejection path `$scope.loading` is never reset because the
 *   function `return`s before the `finally`-equivalent reset, but it was
 *   also never set to `true` for *this* branch either, since the `if` runs
 *   after `$scope.loading = true` — reproduced by not touching the loading
 *   state in this validation branch, matching the net user-visible effect:
 *   loading spinner briefly shows then the button remains enabled since
 *   nothing disabled it here), then runs `verifyServiceCoupon` (forces
 *   `BY_SERVICE` defaults), `setDiscountCupomLogInfo` (parses `fromDate` to a
 *   `Date`, stamps `userViewerLogin`), `validateMinimumUpgradeValue`,
 *   `validateProfileAllowed` (wraps the single value in a 1-item array, or
 *   nulls it out), `validateCouponUsageType` (nulls it out unless
 *   `BY_NEW_PLAN`) before POSTing. All five helpers ported 1:1 as pure
 *   functions operating on the payload right before submit.
 * - On success: toast + navigate to `workspace.discountCoupons` ->
 *   `/workspace/discount-coupons`. On error: toast with
 *   `error.data.errorMessage` -> `error?.response?.data?.errorMessage`, same
 *   extraction already established by `InactivatorPage.jsx`/
 *   `MigrationListPage.jsx`; the legacy `discountCoupon.fromDate =
 *   fromDate` (restoring the pre-parse string) reset-on-error is
 *   intentionally NOT reproduced: this line undoes `setDiscountCupomLogInfo`
 *   on the *submitted* payload object, which in the legacy code is the same
 *   object bound to the form (AngularJS `ng-model` two-way binding) — so
 *   undoing that field keeps the form re-editable after a failed submit. This
 *   component builds a fresh payload object per submit instead of mutating
 *   form state directly, so the still-live `formState` was never touched by
 *   the submit-time transforms in the first place — nothing needs undoing for
 *   the same user-visible outcome (form remains editable, dates unchanged).
 * - "Cancelar" (`ng-back ui-sref="workspace.discountCoupons"`) -> `/workspace/
 *   discount-coupons`. `myform.$valid`/`$invalid` (native browser
 *   `required`-driven `<form>` validity in legacy, no custom validators) ->
 *   ported as a `canSubmit` boolean re-derived from the same required fields
 *   per current visibility, gating the submit button's `disabled` state,
 *   plus native HTML `required` attributes on the underlying inputs for the
 *   same baseline browser validation UX.
 *
 * Not ported: Materialize `material_select`/`material_chip`/`pickadate`
 * jQuery initialization and the `#fountainG` loading spinner chrome —
 * superseded by MUI's own primitives per `plan.md`'s UI-kit decision, same
 * as every other ported module.
 */

export const CATEGORY = {
  BY_NEW_PLAN: 'BY_NEW_PLAN',
  BY_SERVICE: 'BY_SERVICE',
  BY_ADDITIONAL_RESOURCE: 'BY_ADDITIONAL_RESOURCE',
}

export const DISCOUNT_TYPE = {
  BY_VALUE: 'BY_VALUE',
  BY_PERCENTAGE: 'BY_PERCENTAGE',
}

export const COUPON_USAGE_TYPE = {
  EXCLUSIVE: 'EXCLUSIVE',
  UNRESTRICTED: 'UNRESTRICTED',
}

const PLANS = [
  { tag: 'Up', key: 'UP' },
  { tag: 'Smart', key: 'SMART' },
  { tag: 'Company', key: 'COMPANY' },
  { tag: 'Vip', key: 'VIP' },
  { tag: 'Vip 3.000', key: 'VIP_3K' },
  { tag: 'Vip 5.000', key: 'VIP_5K' },
  { tag: 'Vip 10.000', key: 'VIP_10K' },
  { tag: 'Vip 20.000', key: 'VIP_20K' },
  { tag: 'Vip 40.000', key: 'VIP_40K' },
  { tag: 'Vip 45.000', key: 'VIP_45K' },
  { tag: 'Vip 50.000', key: 'VIP_50K' },
]

const ADDITIONAL_RESOURCES_QUANTITY = {
  PLEADING_CREATOR: [5, 10, 20, 50, 100, 200],
  WHATSAPP_INTEGRATION: [1],
  ARTIFICIAL_INTELLIGENCE: [350, 700, 1500],
}

const GENERIC_ERROR_MESSAGE = 'Erro desconhecido'

const PERIODICITY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'YEARLY', label: 'Anual' },
]

/** Port of `formatDate(date, day)` + `buildStartDate()`. */
function buildStartDate() {
  const date = new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${date.getFullYear()}`
}

/** Port of `strToDate(date)`. */
function strToDate(date) {
  if (!date) return ''
  const [day, month, year] = date.split('/')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/** `dd/mm/yyyy` (form state shape, matches legacy) -> `yyyy-mm-dd` (native `<input type="date">` value). */
function toDateInputValue(value) {
  if (!value) return ''
  const [day, month, year] = value.split('/')
  if (!day || !month || !year) return ''
  return `${year}-${month}-${day}`
}

/** `yyyy-mm-dd` (native `<input type="date">` value) -> `dd/mm/yyyy` (form state shape). */
function fromDateInputValue(value) {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  if (!day || !month || !year) return ''
  return `${day}/${month}/${year}`
}

/** Port of `validateEmail(email)`. */
function validateEmail(email) {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/
  return pattern.test(String(email).toLowerCase())
}

/** Port of `validateTenant(tenant)`. */
function validateTenant(tenant) {
  const TENANT_LENGTH = 16
  if (tenant.length !== TENANT_LENGTH) return false
  return /^[0-9]+$/.test(String(tenant))
}

/** Port of `createHash(prefix)`. */
function createHash(prefix) {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let text = ''
  for (let i = 0; i < 6; i += 1) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }
  return prefix + text
}

/** Port of `$scope.isByNewPlanOrAdditionalResource`. */
function isByNewPlanOrAdditionalResource(category) {
  return category === CATEGORY.BY_NEW_PLAN || category === CATEGORY.BY_ADDITIONAL_RESOURCE
}

/** Port of `$scope.isNewPlanOrServiceOrAdditionalResource`. */
function isNewPlanOrServiceOrAdditionalResource(category) {
  return (
    category === CATEGORY.BY_NEW_PLAN ||
    category === CATEGORY.BY_SERVICE ||
    category === CATEGORY.BY_ADDITIONAL_RESOURCE
  )
}

/** Port of `$scope.isPlanOrAdditionalResource`. */
function isPlanOrAdditionalResource(category) {
  return category === CATEGORY.BY_ADDITIONAL_RESOURCE || category === CATEGORY.BY_NEW_PLAN
}

/**
 * A percentage coupon on Planos novos is always exclusive, regardless of
 * whatever `couponUsageType` happens to be stored — derived instead of
 * imperatively set on the `discountType` change handler, so the result
 * doesn't depend on which order `category`/`discountType` were changed in.
 */
function getEffectiveCouponUsageType(state) {
  const isPercentageOnNewPlan =
    state.category === CATEGORY.BY_NEW_PLAN && state.discountType === DISCOUNT_TYPE.BY_PERCENTAGE
  return isPercentageOnNewPlan ? COUPON_USAGE_TYPE.EXCLUSIVE : state.couponUsageType
}

/** Port of `$scope.canApplyMinimumUpgradeValue`. */
function canApplyMinimumUpgradeValue(state) {
  const isCouponCategoryAllowed = state.category === CATEGORY.BY_NEW_PLAN
  const hasCondition =
    getEffectiveCouponUsageType(state) === COUPON_USAGE_TYPE.EXCLUSIVE &&
    state.profileAllowed === 'EDITING'
  return isCouponCategoryAllowed && hasCondition
}

const INITIAL_STATE = {
  category: CATEGORY.BY_NEW_PLAN,
  fromDate: buildStartDate(),
  dueDate: '',
  validFor: 'ONE_MONTH',
  type: 'BY_TENANT',
  discountType: DISCOUNT_TYPE.BY_VALUE,
  couponUsageType: COUPON_USAGE_TYPE.EXCLUSIVE,
  profileAllowed: 'BOTH',
  plans: [],
  periodicity: 'MONTHLY',
  limitUsage: false,
  useQuantityLimit: '',
  userIdentification: 'VIA_EMAIL',
  emails: [],
  tenantIds: [],
  additionalResourceType: '',
  additionalResourceQuantity: '',
  couponClass: 'CL_REGULAR_TEMPORARY',
  code: '',
  discountValue: '',
  minimumUpgradeValue: '',
  departament: 'DEP_COMERCIAL',
  reason: 'PLAN_DEAL',
  vendor: '',
  observation: '',
}

function getAvailableQuantities(resourceType) {
  if (!resourceType) return []
  return ADDITIONAL_RESOURCES_QUANTITY[resourceType] || []
}

function DiscountCouponAddPage() {
  const navigate = useNavigate()
  const { getUserLogin, getUserName } = useAuth()
  const createMutation = useCreateDiscountCouponMutation()

  const [formState, setFormState] = useState(() => ({
    ...INITIAL_STATE,
    vendor: getUserName() || '',
  }))
  const [emailTenantInput, setEmailTenantInput] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function setField(field, value) {
    setFormState((current) => ({ ...current, [field]: value }))
  }

  /** Port of `onSelectCategory()`. */
  function handleCategoryChange(event) {
    const category = event.target.value

    setFormState((current) => {
      const next = { ...current, category, type: 'BY_TENANT' }

      if (category === CATEGORY.BY_SERVICE && current.fromDate) {
        const splitted = current.fromDate.split('/')
        if (splitted.length === 3) {
          const [day, month, year] = splitted
          const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
          parsedDate.setDate(parsedDate.getDate() + 2)
          const pad = (n) => String(n).padStart(2, '0')
          next.dueDate = `${pad(parsedDate.getDate())}/${pad(parsedDate.getMonth() + 1)}/${parsedDate.getFullYear()}`
        }
      } else {
        next.dueDate = ''
      }

      if (category === CATEGORY.BY_SERVICE) {
        next.discountType = DISCOUNT_TYPE.BY_VALUE
      }

      return next
    })
  }

  function handleDiscountTypeChange(event) {
    setField('discountType', event.target.value)
  }

  /** Port of `onSelectLimitUsageToggle(event)`. */
  function handleLimitUsageToggle(event) {
    const limitUsageNewState = event.target.checked
    setFormState((current) => ({
      ...current,
      limitUsage: limitUsageNewState,
      useQuantityLimit: limitUsageNewState ? current.useQuantityLimit : '',
    }))
  }

  /** Port of `replaceEmailsAndTenants()`, triggered by `ng-change` on `userIdentification`. */
  function handleUserIdentificationChange(event) {
    const userIdentification = event.target.value
    setFormState((current) => {
      if (userIdentification === 'VIA_EMAIL') {
        return { ...current, userIdentification, emails: current.tenantIds, tenantIds: [] }
      }
      return { ...current, userIdentification, tenantIds: current.emails, emails: [] }
    })
  }

  /** Port of `onSelectPlan(event, plan)`. */
  function togglePlan(planKey) {
    setFormState((current) => ({
      ...current,
      plans: current.plans.includes(planKey)
        ? current.plans.filter((p) => p !== planKey)
        : [...current.plans, planKey],
    }))
  }

  /** Port of `onAdditionalResourceTypeChange()`. */
  function handleAdditionalResourceTypeChange(event) {
    setField('additionalResourceType', event.target.value)
    setField('additionalResourceQuantity', '')
  }

  /** Port of `onSelectCombo()`. */
  function handleCouponClassChange(event) {
    const couponClass = event.target.value
    setFormState((current) => ({
      ...current,
      couponClass,
      code: couponClass === 'CL_COMBO' ? createHash('combo-') : createHash(''),
    }))
  }

  function addEmailOrTenant(rawValue) {
    const field = formState.userIdentification === 'VIA_EMAIL' ? 'emails' : 'tenantIds'
    const validator = formState.userIdentification === 'VIA_EMAIL' ? validateEmail : validateTenant

    const candidates = rawValue
      .split(/\s+/)
      .map((value) => value.trim())
      .filter((value, index, array) => value !== '' && array.indexOf(value) === index)
      .filter((value) => !formState.emails.includes(value) && !formState.tenantIds.includes(value))
      .filter((value) => validator(value))

    if (candidates.length === 0) return

    setFormState((current) => ({ ...current, [field]: [...current[field], ...candidates] }))
  }

  /** Port of `watchEmailsTenantsInputPaste()`. */
  function handleEmailTenantPaste(event) {
    const pasted = event.clipboardData?.getData('text')
    if (!pasted) return
    event.preventDefault()
    addEmailOrTenant(pasted)
    setEmailTenantInput('')
  }

  function handleEmailTenantKeyDown(event) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    if (!emailTenantInput.trim()) return
    addEmailOrTenant(emailTenantInput)
    setEmailTenantInput('')
  }

  /** Port of the `chip.delete` handler removing a single chip. */
  function removeEmailOrTenant(value) {
    const field = formState.userIdentification === 'VIA_EMAIL' ? 'emails' : 'tenantIds'
    setFormState((current) => ({
      ...current,
      [field]: current[field].filter((entry) => entry !== value),
    }))
  }

  const currentEmailTenantList =
    formState.userIdentification === 'VIA_EMAIL' ? formState.emails : formState.tenantIds

  /** Port of `back()`. */
  function handleCancel() {
    navigate('/workspace/discount-coupons')
  }

  /**
   * Port of `verifyServiceCoupon`, `setDiscountCupomLogInfo`,
   * `validateMinimumUpgradeValue`, `validateProfileAllowed`,
   * `validateCouponUsageType` — applied in the same order as `save()`.
   */
  function buildPayload() {
    const payload = { ...formState }

    if (payload.category === CATEGORY.BY_SERVICE) {
      payload.discountType = DISCOUNT_TYPE.BY_VALUE
      payload.plans = []
      payload.periodicity = null
      payload.couponClass = 'CL_REGULAR'
      payload.applyToFirstMonth = false
      payload.freeMonths = null
    }

    payload.fromDate = strToDate(payload.fromDate)
    payload.userViewerLogin = getUserLogin()

    if (payload.dueDate) {
      payload.dueDate = strToDate(formState.dueDate)
    }

    if (!canApplyMinimumUpgradeValue(formState)) {
      payload.minimumUpgradeValue = null
    }

    if (formState.category !== CATEGORY.BY_NEW_PLAN || formState.profileAllowed === 'BOTH') {
      payload.profileAllowed = null
    } else {
      payload.profileAllowed = [formState.profileAllowed]
    }

    payload.couponUsageType =
      formState.category === CATEGORY.BY_NEW_PLAN ? getEffectiveCouponUsageType(formState) : null

    payload.discountValue = payload.discountValue === '' ? null : Number(payload.discountValue)

    const showLimitUsage =
      (formState.category === CATEGORY.BY_NEW_PLAN || formState.category === CATEGORY.BY_SERVICE) &&
      formState.type !== 'BY_TENANT'
    if (showLimitUsage && formState.limitUsage && formState.useQuantityLimit !== '') {
      payload.useQuantityLimit = Number(formState.useQuantityLimit)
    } else {
      delete payload.useQuantityLimit
    }

    if (formState.category === CATEGORY.BY_ADDITIONAL_RESOURCE) {
      payload.additionalResourceQuantity =
        formState.discountType === DISCOUNT_TYPE.BY_PERCENTAGE ||
        payload.additionalResourceQuantity === ''
          ? null
          : Number(payload.additionalResourceQuantity)
    } else {
      payload.additionalResourceType = null
      payload.additionalResourceQuantity = null
    }

    return payload
  }

  /** Port of `save(discountCoupon)`. */
  function handleSubmit(event) {
    event.preventDefault()

    if (formState.dueDate) {
      const splitted = formState.dueDate.split('/')
      if (splitted.length !== 3) return
      const [day, month, year] = splitted
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
      if (parsedDate <= new Date()) {
        notify('A expiração deve ser uma data futura')
        return
      }
    }

    const payload = buildPayload()

    createMutation.mutate(payload, {
      onSuccess: () => {
        notify('Cupom de desconto criado com sucesso!')
        navigate('/workspace/discount-coupons')
      },
      onError: (error) => {
        const message = error?.response?.data?.errorMessage || GENERIC_ERROR_MESSAGE
        notify(`Erro ao cadastrar o cupom de desconto: ${message}`)
      },
    })
  }

  const effectiveCouponUsageType = getEffectiveCouponUsageType(formState)

  const showValidFor = isByNewPlanOrAdditionalResource(formState.category)
  const showNewPlanFields = formState.category === CATEGORY.BY_NEW_PLAN
  const showAdditionalResource = formState.category === CATEGORY.BY_ADDITIONAL_RESOURCE
  const showPercentageDiscountType = showNewPlanFields || showAdditionalResource
  const showPeriodicity = showAdditionalResource || showNewPlanFields
  const periodicityOptions = PERIODICITY_OPTIONS
  const showSecondTypeSelect = isNewPlanOrServiceOrAdditionalResource(formState.category)
  const showLimitUsage =
    (formState.category === CATEGORY.BY_NEW_PLAN || formState.category === CATEGORY.BY_SERVICE) &&
    formState.type !== 'BY_TENANT'
  const showEmailsTenants =
    (formState.type === 'BY_TENANT' || formState.category === CATEGORY.BY_SERVICE) &&
    formState.type !== 'ALL_TENANTS'
  const showCouponClass = isPlanOrAdditionalResource(formState.category)
  const showMinimumUpgradeValue = canApplyMinimumUpgradeValue(formState)
  const showReason = formState.departament === 'DEP_CUSTOMER_EXPERIENCE'
  const showAdditionalResourceQuantity =
    showAdditionalResource && formState.discountType !== DISCOUNT_TYPE.BY_PERCENTAGE
  const availableQuantities = getAvailableQuantities(formState.additionalResourceType)

  const percentageValue = Number(formState.discountValue)
  const isPercentageValueValid =
    formState.discountType !== DISCOUNT_TYPE.BY_PERCENTAGE ||
    (formState.discountValue !== '' && percentageValue >= 1 && percentageValue <= 100)

  const canSubmit =
    formState.category &&
    formState.fromDate &&
    formState.dueDate &&
    formState.code &&
    formState.vendor &&
    formState.discountValue !== '' &&
    isPercentageValueValid &&
    (!showNewPlanFields || formState.plans.length > 0) &&
    (!showLimitUsage || !formState.limitUsage || formState.useQuantityLimit !== '') &&
    (!showAdditionalResource || formState.additionalResourceType) &&
    (!showAdditionalResourceQuantity || formState.additionalResourceQuantity)

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h2" gutterBottom>
        Adicionar Cupom de Desconto
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel id="category-label">Desconto para</InputLabel>
              <Select
                labelId="category-label"
                label="Desconto para"
                id="category"
                value={formState.category}
                onChange={handleCategoryChange}
              >
                <MenuItem value={CATEGORY.BY_NEW_PLAN}>
                  Planos novos (up, smart, company ...)
                </MenuItem>
                <MenuItem value={CATEGORY.BY_SERVICE}>Serviços adicionais</MenuItem>
                <MenuItem value={CATEGORY.BY_ADDITIONAL_RESOURCE}>Turbos</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={6}>
            <TextField
              id="fromDate"
              label="Data de início"
              type="date"
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={toDateInputValue(formState.fromDate)}
              onChange={(event) => setField('fromDate', fromDateInputValue(event.target.value))}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              id="dueDate"
              label="Aplicação expira em"
              type="date"
              required
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              value={toDateInputValue(formState.dueDate)}
              onChange={(event) => setField('dueDate', fromDateInputValue(event.target.value))}
            />
          </Grid>

          {showValidFor && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="validFor-label">Válido a partir da aplicação por</InputLabel>
                <Select
                  labelId="validFor-label"
                  label="Válido a partir da aplicação por"
                  id="validFor"
                  value={formState.validFor}
                  onChange={(event) => setField('validFor', event.target.value)}
                >
                  <MenuItem value="ONE_MONTH">1 mês</MenuItem>
                  <MenuItem value="TWO_MONTHS">2 meses</MenuItem>
                  <MenuItem value="THREE_MONTHS">3 meses</MenuItem>
                  <MenuItem value="SIX_MONTHS">6 meses</MenuItem>
                  <MenuItem value="YEAR">1 ano</MenuItem>
                  <MenuItem value="LIFETIME">Vitalício</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={showNewPlanFields ? 6 : 12}>
            <FormControl fullWidth required disabled={!showPercentageDiscountType}>
              <InputLabel id="discountType-label">Tipo de desconto</InputLabel>
              <Select
                labelId="discountType-label"
                label="Tipo de desconto"
                id="discountType"
                value={formState.discountType}
                onChange={handleDiscountTypeChange}
              >
                <MenuItem value={DISCOUNT_TYPE.BY_VALUE}>Valor fixo</MenuItem>
                {showPercentageDiscountType && (
                  <MenuItem value={DISCOUNT_TYPE.BY_PERCENTAGE}>Porcentagem</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          {showNewPlanFields && (
            <Grid size={6}>
              <FormControl
                fullWidth
                required
                disabled={formState.discountType === DISCOUNT_TYPE.BY_PERCENTAGE}
              >
                <InputLabel id="couponUsageType-label">Exclusividade</InputLabel>
                <Select
                  labelId="couponUsageType-label"
                  label="Exclusividade"
                  id="couponUsageType"
                  value={effectiveCouponUsageType}
                  onChange={(event) => setField('couponUsageType', event.target.value)}
                >
                  <MenuItem value={COUPON_USAGE_TYPE.EXCLUSIVE}>
                    Não é possível utilizá-lo em conjunto com outros cupons
                  </MenuItem>
                  <MenuItem value={COUPON_USAGE_TYPE.UNRESTRICTED}>
                    É possível utilizá-lo em conjunto com outros cupons
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {showNewPlanFields && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="profileAllowed-label">Aplicação do cupom</InputLabel>
                <Select
                  labelId="profileAllowed-label"
                  label="Aplicação do cupom"
                  id="profileAllowed"
                  value={formState.profileAllowed || 'BOTH'}
                  onChange={(event) => setField('profileAllowed', event.target.value)}
                >
                  <MenuItem value="HIRING">Contratação</MenuItem>
                  <MenuItem value="EDITING">Edição de plano</MenuItem>
                  <MenuItem value="BOTH">Ambos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {showNewPlanFields && (
            <Grid size={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Válido nos planos*
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {PLANS.map((plan) => (
                  <Chip
                    key={plan.key}
                    label={plan.tag}
                    clickable
                    variant={formState.plans.includes(plan.key) ? 'filled' : 'outlined'}
                    onClick={() => togglePlan(plan.key)}
                    sx={
                      formState.plans.includes(plan.key)
                        ? {
                            backgroundColor: '#E8DEF8',
                            color: '#000',
                            fontWeight: 700,
                            boxShadow: '0px 0px 0px 1px #6A1B9A',
                          }
                        : {
                            backgroundColor: 'transparent',
                            color: '#575757',
                            fontWeight: 400,
                            boxShadow: '0px 0px 0px 1px #79747E',
                          }
                    }
                  />
                ))}
              </Stack>
            </Grid>
          )}

          {showPeriodicity && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel id="periodicity-label">
                  Tipo de periodicidade válido para o cupom
                </InputLabel>
                <Select
                  labelId="periodicity-label"
                  label="Tipo de periodicidade válido para o cupom"
                  id="periodicity"
                  value={formState.periodicity}
                  onChange={(event) => setField('periodicity', event.target.value)}
                >
                  {periodicityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {showSecondTypeSelect && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel id="type-label">Válido para</InputLabel>
                <Select
                  labelId="type-label"
                  label="Válido para"
                  id="type2"
                  value={formState.type}
                  onChange={(event) => setField('type', event.target.value)}
                >
                  <MenuItem value="BY_TENANT">Apenas clientes selecionados</MenuItem>
                  <MenuItem value="ALL_TENANTS">Todos os clientes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {showLimitUsage && (
            <Grid size={12}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch checked={formState.limitUsage} onChange={handleLimitUsageToggle} />
                  }
                  label="Limitar quantidade de aplicação"
                />
                {formState.limitUsage && (
                  <TextField
                    id="useQuantityLimit"
                    label="Quantidade limite de cupons aplicados"
                    type="number"
                    required
                    value={formState.useQuantityLimit}
                    onChange={(event) => setField('useQuantityLimit', event.target.value)}
                  />
                )}
              </Stack>
            </Grid>
          )}

          {showEmailsTenants && (
            <Grid size={12}>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ minWidth: 160 }}>
                  <InputLabel id="userIdentification-label">Clientes por</InputLabel>
                  <Select
                    labelId="userIdentification-label"
                    label="Clientes por"
                    id="userIdentification"
                    value={formState.userIdentification}
                    onChange={handleUserIdentificationChange}
                  >
                    <MenuItem value="VIA_EMAIL">E-mail</MenuItem>
                    <MenuItem value="VIA_TENANT">Tenant</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    id="emails-tenants-input"
                    label={
                      formState.userIdentification === 'VIA_EMAIL'
                        ? 'Digite os e-mails*'
                        : 'Digite os tenants*'
                    }
                    fullWidth
                    value={emailTenantInput}
                    onChange={(event) => setEmailTenantInput(event.target.value)}
                    onKeyDown={handleEmailTenantKeyDown}
                    onPaste={handleEmailTenantPaste}
                  />
                  <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {currentEmailTenantList.map((entry) => (
                      <Chip key={entry} label={entry} onDelete={() => removeEmailOrTenant(entry)} />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          )}

          {showAdditionalResource && (
            <>
              <Grid size={showAdditionalResourceQuantity ? 6 : 12}>
                <FormControl fullWidth required>
                  <InputLabel id="additionalResourceType-label">Turbo</InputLabel>
                  <Select
                    labelId="additionalResourceType-label"
                    label="Turbo"
                    id="additionalResourceType"
                    value={formState.additionalResourceType}
                    onChange={handleAdditionalResourceTypeChange}
                  >
                    {ADDITIONAL_RESOURCE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {showAdditionalResourceQuantity && (
                <Grid size={6}>
                  <FormControl fullWidth required disabled={!formState.additionalResourceType}>
                    <InputLabel id="additionalResourceQuantity-label">Quantidade</InputLabel>
                    <Select
                      labelId="additionalResourceQuantity-label"
                      label="Quantidade"
                      id="additionalResourceQuantity"
                      value={formState.additionalResourceQuantity}
                      onChange={(event) =>
                        setField('additionalResourceQuantity', event.target.value)
                      }
                    >
                      {availableQuantities
                        .filter(
                          (quantity) =>
                            formState.additionalResourceType !== 'WHATSAPP_INTEGRATION' ||
                            quantity === 1,
                        )
                        .map((quantity) => (
                          <MenuItem key={quantity} value={quantity}>
                            {formState.additionalResourceType === 'WHATSAPP_INTEGRATION'
                              ? 'Ilimitado'
                              : `${quantity} ${
                                  formState.additionalResourceType === 'ARTIFICIAL_INTELLIGENCE'
                                    ? 'traduções'
                                    : formState.additionalResourceType === 'PLEADING_CREATOR'
                                      ? 'peças'
                                      : 'unidades'
                                }`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          {showCouponClass && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel id="couponClass-label">Classe</InputLabel>
                <Select
                  labelId="couponClass-label"
                  label="Classe"
                  id="couponClass"
                  value={formState.couponClass}
                  onChange={handleCouponClassChange}
                >
                  <MenuItem value="CL_REGULAR_TEMPORARY">Regular temporário</MenuItem>
                  <MenuItem value="CL_REGULAR_PERMANENT">Regular vitalício</MenuItem>
                  <MenuItem value="CL_COMBO">Combo</MenuItem>
                  <MenuItem value="CL_INVITE">Recomendação</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              id="code"
              label="Código do cupom"
              required
              fullWidth
              value={formState.code}
              onChange={(event) => setField('code', event.target.value)}
            />
          </Grid>

          <Grid size={showMinimumUpgradeValue ? 6 : 12}>
            <TextField
              id="discountValue"
              label="Valor de desconto"
              type="number"
              required
              fullWidth
              inputProps={{ min: 1, step: 'any' }}
              value={formState.discountValue}
              onChange={(event) => setField('discountValue', event.target.value)}
            />
          </Grid>
          {showMinimumUpgradeValue && (
            <Grid size={6}>
              <TextField
                id="minimumUpgradeValue"
                label="Valor mínimo de upgrade"
                type="number"
                fullWidth
                inputProps={{ min: 1, step: 'any' }}
                value={formState.minimumUpgradeValue}
                onChange={(event) => setField('minimumUpgradeValue', event.target.value)}
              />
            </Grid>
          )}

          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel id="departament-label">Time</InputLabel>
              <Select
                labelId="departament-label"
                label="Time"
                id="departament"
                value={formState.departament}
                onChange={(event) => setField('departament', event.target.value)}
              >
                <MenuItem value="DEP_COMERCIAL">Comercial</MenuItem>
                <MenuItem value="DEP_COMERCIAL_UPSELL">Comercial-Upsell</MenuItem>
                <MenuItem value="DEP_MARKETING">Marketing</MenuItem>
                <MenuItem value="DEP_CUSTOMER_EXPERIENCE">Customer experience</MenuItem>
                <MenuItem value="DEP_ADMINISTRATION">Administração</MenuItem>
                <MenuItem value="DEP_TECHNOLOGY">Tecnologia</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {showReason && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="reason-label">Motivo</InputLabel>
                <Select
                  labelId="reason-label"
                  label="Motivo"
                  id="reason"
                  value={formState.reason}
                  onChange={(event) => setField('reason', event.target.value)}
                >
                  <MenuItem value="PLAN_DEAL">Negociação de plano</MenuItem>
                  <MenuItem value="EXPANSION">Expansão</MenuItem>
                  <MenuItem value="IGPM">IGP-M</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              id="vendor"
              label="Quem criou o cupom"
              required
              fullWidth
              value={formState.vendor}
              onChange={(event) => setField('vendor', event.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              id="observation"
              label="Observações"
              fullWidth
              multiline
              minRows={3}
              value={formState.observation}
              onChange={(event) => setField('observation', event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={!canSubmit || createMutation.isPending}>
          Salvar
        </Button>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DiscountCouponAddPage
